/**
 * Listado de roles del sistema (Console superadmin).
 *
 * Por default muestra los **canónicos** (sin empresa) — los que aplican
 * a todos los tenants. Si la URL trae ``?empresa=N``, muestra los del
 * tenant + canónicos disponibles.
 */
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  Eye,
  EyeOff,
  Pause,
  Pencil,
  Play,
  Plus,
  ShieldCheck,
} from "lucide-react";

import { useToast } from "../../components/Toast";
import {
  Badge,
  Button,
  Cargando,
  ErrorCarga,
  PageHeader,
  TableWrap,
} from "../../components/ui";
import { ApiError } from "../../lib/api";
import {
  actualizarRol,
  detalleEmpresa,
  listarRolesCanonicos,
  listarRolesEmpresa,
  type RolRead,
} from "./api";
import { RolFormModal } from "./RolFormModal";

export function RolesListPage(): JSX.Element {
  const [params] = useSearchParams();
  const empresaIdRaw = params.get("empresa");
  const empresaId = empresaIdRaw ? Number(empresaIdRaw) : null;
  const isCanonicoView = empresaId === null;

  const queryClient = useQueryClient();
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<RolRead | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const empresa = useQuery({
    queryKey: ["superadmin", "empresa", empresaId],
    queryFn: () => detalleEmpresa(empresaId as number),
    enabled: empresaId !== null,
  });

  const roles = useQuery({
    queryKey: isCanonicoView
      ? ["superadmin", "roles-canonicos"]
      : ["superadmin", "roles-empresa", empresaId],
    queryFn: () =>
      isCanonicoView
        ? listarRolesCanonicos()
        : listarRolesEmpresa(empresaId as number),
  });

  const toggleActiveMut = useMutation({
    mutationFn: ({ rolId, active }: { rolId: number; active: boolean }) =>
      actualizarRol(rolId, { is_active: active }),
    onSuccess: () => {
      toast.success("Rol actualizado");
      void queryClient.invalidateQueries({ queryKey: ["superadmin"] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "No se pudo actualizar"),
  });

  const items = roles.data ?? [];
  const activos = items.filter((r) => r.is_active);
  const inactivos = items.filter((r) => !r.is_active);

  const titulo = isCanonicoView
    ? "Roles y permisos"
    : `Roles del comité · ${empresa.data?.empresa.nombre ?? "…"}`;

  const subtitulo = isCanonicoView
    ? "Roles canónicos del sistema: aplican a todos los comités. Si un tenant define un rol con el mismo code, gana el del tenant."
    : "Combina canónicos heredados + roles propios del comité (sobreescriben canónicos con el mismo code).";

  return (
    <div className="space-y-5 max-w-5xl">
      <PageHeader
        title={titulo}
        icon={ShieldCheck}
        volver={
          isCanonicoView
            ? undefined
            : { etiqueta: empresa.data?.empresa.nombre ?? "el comité", ruta: `/superadmin/empresas/${empresaId}` }
        }
        subtitle={
          <>
            {subtitulo}
            <span className="mt-1 block text-xs text-neutral-500">
              {activos.length} activos · {inactivos.length} inactivos
            </span>
          </>
        }
        actions={
          <Button icon={Plus} onClick={() => setCreating(true)}>
            {isCanonicoView ? "Nuevo rol canónico" : "Nuevo rol del comité"}
          </Button>
        }
      />

      <TableWrap label="Roles">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-left text-xs uppercase text-neutral-500">
            <tr>
              <th scope="col" className="px-3 py-2"></th>
              <th scope="col" className="px-3 py-2">Code</th>
              <th scope="col" className="px-3 py-2">Nombre</th>
              <th scope="col" className="px-3 py-2">Scope</th>
              <th scope="col" className="px-3 py-2 text-right">Permisos</th>
              <th scope="col" className="px-3 py-2">Estado</th>
              <th scope="col" className="px-3 py-2 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {roles.isLoading && (
              <tr>
                <td colSpan={7} className="px-3">
                  <Cargando />
                </td>
              </tr>
            )}
            {roles.isError && (
              <tr>
                <td colSpan={7} className="px-3 py-4">
                  <ErrorCarga error={roles.error} onReintentar={() => roles.refetch()} />
                </td>
              </tr>
            )}
            {!roles.isLoading &&
              items.map((r) => {
                const isOpen = expanded.has(r.id);
                const wildcard = r.permisos.includes("*");
                return (
                  <>
                    <tr key={r.id} className="border-t border-neutral-100 hover:bg-neutral-50">
                      <td className="px-3 py-2 w-8">
                        <Button
                          variant="ghost"
                          size="sm"
                          iconOnly
                          icon={isOpen ? EyeOff : Eye}
                          onClick={() => toggleSet(setExpanded, r.id)}
                          aria-label={isOpen ? "Contraer permisos" : "Ver permisos"}
                          title={isOpen ? "Contraer permisos" : "Ver permisos"}
                        />
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{r.code}</td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-neutral-900">{r.nombre}</div>
                        {r.descripcion && (
                          <div className="text-xs text-neutral-500">{r.descripcion}</div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {r.empresa_id === null ? (
                          <Badge tone="amber">Canónico</Badge>
                        ) : (
                          <Badge tone="brand">Comité</Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {wildcard ? (
                          <span className="font-bold text-amber-700">★ TODOS</span>
                        ) : (
                          r.permisos.length
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {r.is_active ? (
                          <Badge tone="emerald">
                            <CheckCircle2 className="h-3 w-3" strokeWidth={1.5} /> Activo
                          </Badge>
                        ) : (
                          <Badge tone="slate">Inactivo</Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-1">
                          <Button
                            variant="secondary"
                            size="sm"
                            icon={Pencil}
                            onClick={() => setEditing(r)}
                          >
                            Editar
                          </Button>
                          {r.is_active ? (
                            <button
                              type="button"
                              onClick={() =>
                                toggleActiveMut.mutate({ rolId: r.id, active: false })
                              }
                              disabled={toggleActiveMut.isPending}
                              className="inline-flex items-center gap-1 rounded-md bg-rose-50 px-2.5 py-1 text-xs font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                            >
                              <Pause className="h-3.5 w-3.5" strokeWidth={1.5} />
                              Desactivar
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() =>
                                toggleActiveMut.mutate({ rolId: r.id, active: true })
                              }
                              disabled={toggleActiveMut.isPending}
                              className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                            >
                              <Play className="h-3.5 w-3.5" strokeWidth={1.5} />
                              Reactivar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-neutral-50">
                        <td></td>
                        <td colSpan={6} className="px-3 py-2">
                          <div className="text-xs text-neutral-700 flex flex-wrap gap-1.5">
                            {wildcard ? (
                              <span className="font-medium text-amber-700">
                                ★ Wildcard — todos los permisos del sistema
                              </span>
                            ) : r.permisos.length === 0 ? (
                              <span className="italic text-neutral-500">
                                Sin permisos asignados
                              </span>
                            ) : (
                              r.permisos.map((p) => (
                                <code
                                  key={p}
                                  className="rounded bg-white border border-neutral-200 px-2 py-0.5 text-xs font-mono text-neutral-700"
                                >
                                  {p}
                                </code>
                              ))
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
          </tbody>
        </table>
      </TableWrap>

      {creating && (
        <RolFormModal
          mode={
            isCanonicoView
              ? { kind: "create-canonico" }
              : { kind: "create-empresa", empresaId: empresaId as number }
          }
          onClose={() => setCreating(false)}
          onSuccess={() => {
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: ["superadmin"] });
          }}
        />
      )}
      {editing && (
        <RolFormModal
          mode={{ kind: "edit", rol: editing }}
          onClose={() => setEditing(null)}
          onSuccess={() => {
            setEditing(null);
            void queryClient.invalidateQueries({ queryKey: ["superadmin"] });
          }}
        />
      )}
    </div>
  );
}

function toggleSet(
  setter: React.Dispatch<React.SetStateAction<Set<number>>>,
  id: number,
): void {
  setter((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
}
